// ============================================================
// Engine Pipeline — End-to-End Orchestrator
// Takes a PGN string, runs the complete analysis pipeline,
// and produces a GameBrief.
// ============================================================

import type { GameBrief, GameMetadata, MoveData } from "@shared/types"
import { parsePgn, normalizePgnForHash } from "./pgn-parser"
import { analyzeGame, type ProgressCallback } from "./stockfish"
import { detectPhases, classifyGameArc } from "./story-detection"
import { extractKeyMoments } from "./key-moments"
import {
  assembleGameBrief,
  estimateBriefTokens,
  validateGameBrief,
} from "./brief-assembler"

// --- Types ---

export interface PipelineResult {
  metadata: GameMetadata
  moves: MoveData[]
  brief: GameBrief
  criticalMoveIndex: number
  criticalFen: string
  pgnHash: string
  tokenEstimate: number
  validationErrors: string[]
}

export interface PipelineOptions {
  /** Stockfish analysis depth (default: 16) */
  depth?: number
  /** Progress callback for UI updates */
  onProgress?: ProgressCallback
  /** Skip Stockfish analysis (use mock evals) — for testing */
  skipStockfish?: boolean
  /** Pre-computed eval data (for testing) */
  precomputedMoves?: MoveData[]
}

// --- Pipeline ---

/**
 * Run the complete analysis pipeline.
 * PGN string in -> GameBrief out.
 */
export async function runPipeline(
  pgn: string,
  options: PipelineOptions = {}
): Promise<PipelineResult> {
  const { depth, onProgress, skipStockfish = false, precomputedMoves } = options

  // Step 1: Parse PGN
  const parsed = parsePgn(pgn)

  // Step 2: Stockfish analysis (or use precomputed/mock data)
  let evaluatedMoves: MoveData[]

  if (precomputedMoves) {
    evaluatedMoves = precomputedMoves
  } else if (skipStockfish) {
    // Mock evals for testing (all zeros)
    evaluatedMoves = parsed.moves.map((m) => ({
      ...m,
      eval: 0,
      bestMove: null,
      evalDelta: 0,
    }))
  } else {
    evaluatedMoves = await analyzeGame(
      parsed.moves,
      parsed.startingFen,
      onProgress,
      depth
    )
  }

  // Step 3: Story detection
  const phases = detectPhases(evaluatedMoves)
  const gameArc = classifyGameArc(evaluatedMoves, parsed.metadata.result)

  // Step 4: Key moment extraction
  const { keyMoments, criticalMoveIndex } = extractKeyMoments(evaluatedMoves)

  // Step 5: Assemble GameBrief
  const brief = assembleGameBrief({
    metadata: parsed.metadata,
    moves: evaluatedMoves,
    phases,
    keyMoments,
    gameArc,
  })

  // Step 6: Validate
  const validationErrors = validateGameBrief(brief)
  const tokenEstimate = estimateBriefTokens(brief)

  // Get critical FEN for OG image
  const criticalFen =
    evaluatedMoves[criticalMoveIndex]?.fen ??
    evaluatedMoves[evaluatedMoves.length - 1]?.fen ??
    parsed.startingFen

  // Compute PGN hash for dedup
  const pgnHash = await computePgnHash(pgn)

  return {
    metadata: parsed.metadata,
    moves: evaluatedMoves,
    brief,
    criticalMoveIndex,
    criticalFen,
    pgnHash,
    tokenEstimate,
    validationErrors,
  }
}

/**
 * Run the pipeline without Stockfish — for unit testing the
 * story detection and key moment modules against pre-evaluated data.
 */
export function runPipelineSync(
  pgn: string,
  evaluatedMoves: MoveData[]
): Omit<PipelineResult, "pgnHash"> & { pgnHash: string | null } {
  const parsed = parsePgn(pgn)

  const phases = detectPhases(evaluatedMoves)
  const gameArc = classifyGameArc(evaluatedMoves, parsed.metadata.result)
  const { keyMoments, criticalMoveIndex } = extractKeyMoments(evaluatedMoves)

  const brief = assembleGameBrief({
    metadata: parsed.metadata,
    moves: evaluatedMoves,
    phases,
    keyMoments,
    gameArc,
  })

  const validationErrors = validateGameBrief(brief)
  const tokenEstimate = estimateBriefTokens(brief)

  const criticalFen =
    evaluatedMoves[criticalMoveIndex]?.fen ??
    evaluatedMoves[evaluatedMoves.length - 1]?.fen ??
    parsed.startingFen

  return {
    metadata: parsed.metadata,
    moves: evaluatedMoves,
    brief,
    criticalMoveIndex,
    criticalFen,
    pgnHash: null,
    tokenEstimate,
    validationErrors,
  }
}

// --- Helpers ---

/**
 * Compute SHA-256 hash of normalized PGN for dedup.
 * Uses the Web Crypto API (available in browsers and Workers).
 */
async function computePgnHash(pgn: string): Promise<string> {
  const normalized = normalizePgnForHash(pgn)
  const encoder = new TextEncoder()
  const data = encoder.encode(normalized)

  // Use Web Crypto API
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const hashBuffer = await crypto.subtle.digest("SHA-256", data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
  }

  // Fallback: simple hash for environments without crypto.subtle
  let hash = 0
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i)
    hash = ((hash << 5) - hash + char) | 0
  }
  return Math.abs(hash).toString(16)
}
